import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import StatusBadge from "@/components/StatusBadge";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { fmtDate, fmtDateTime } from "@/lib/constants";
import { Plus, Search, PackageCheck, Briefcase, Clock, CheckCircle2, Sparkles, ArrowRight, Activity } from "lucide-react";
import ActivityRow from "@/components/ActivityRow";

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [activity, setActivity] = useState(null);
  const canSeeActivity = user?.role === "admin" || user?.role === "staff";

  useEffect(() => {
    api.get("/dashboard/staff").then((r) => setData(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (canSeeActivity) api.get("/audit/recent", { params: { limit: 8 } }).then((r) => setActivity(r.data)).catch(() => {});
  }, [canSeeActivity]);

  const stats = [
    { label: "New", sub: "today", value: data?.new_today ?? "—", icon: Sparkles, testId: "stat-new", to: "/jobs?status=New" },
    { label: "Pending", sub: "New + Processing", value: data?.pending ?? "—", icon: Clock, testId: "stat-processing", to: "/jobs?status=Pending" },
    { label: "Ready for Delivery", value: data?.ready ?? "—", icon: PackageCheck, testId: "stat-ready", to: "/jobs?status=Ready" },
    { label: "Delivered", sub: "today", value: data?.completed_today ?? "—", icon: CheckCircle2, testId: "stat-delivered", to: "/jobs?status=Delivered" },
  ];

  const quickActions = [
    { label: "New Job", icon: Plus, to: "/new-job", primary: true, testId: "quick-action-new-job", operatorHidden: true },
    { label: "Search Job", icon: Search, to: "/jobs", testId: "quick-action-search-job" },
    { label: "Search Customer", icon: Search, to: "/customers", testId: "quick-action-search-customer" },
    { label: "View Ready Jobs", icon: PackageCheck, to: "/ready", testId: "quick-action-ready-jobs" },
  ].filter((a) => !(a.operatorHidden && user?.role === "operator"));

  const JobRow = ({ j }) => (
    <button onClick={() => navigate(`/jobs/${j.id}`)} data-testid={`dashboard-job-${j.id}`}
      className="w-full flex items-center gap-3 rounded-xl border bg-card px-4 py-3 hover:bg-accent/50 transition-colors text-left">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold truncate">{j.job_number} · {j.article_name}</p>
        <p className="text-xs text-muted-foreground truncate">{j.customer_name} · {(j.required_finishes || []).join(", ")}</p>
      </div>
      <StatusBadge status={j.status} />
    </button>
  );

  return (
    <div className="space-y-6" data-testid="staff-dashboard">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-amber-700 dark:text-amber-400">Today's Jobs</p>
        <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight mt-1">
          Namaste, {user?.name?.split(" ")[0]}
        </h1>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {stats.map((s) => (
          <Card key={s.label} data-testid={s.testId} className="cursor-pointer hover:shadow-md hover:border-primary/40 transition-all active:scale-[0.98]" onClick={() => navigate(s.to)}>
            <CardContent className="p-4 sm:p-5">
              <s.icon className="h-5 w-5 text-primary mb-2" />
              <p className="text-2xl sm:text-3xl font-bold font-display">{s.value}</p>
              <p className="text-xs text-muted-foreground font-medium mt-1">{s.label}{s.sub ? <span className="font-normal"> ({s.sub})</span> : ""}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div>
        <h2 className="text-base md:text-lg font-semibold mb-3">Quick Actions</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {quickActions.map((a) => (
            <Button key={a.label} data-testid={a.testId} onClick={() => navigate(a.to)}
              variant={a.primary ? "default" : "outline"}
              className="h-14 sm:h-12 text-sm font-semibold gap-2">
              <a.icon className="h-4 w-4" /> {a.label}
            </Button>
          ))}
        </div>
      </div>

      {data?.my_jobs?.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base md:text-lg font-semibold">Assigned to Me</h2>
            <Link to="/jobs" className="text-xs font-semibold text-primary flex items-center gap-1" data-testid="view-all-jobs-link">All jobs <ArrowRight className="h-3 w-3" /></Link>
          </div>
          <div className="space-y-2">{data.my_jobs.slice(0, 5).map((j) => <JobRow key={j.id} j={j} />)}</div>
        </div>
      )}

      <div>
        <h2 className="text-base md:text-lg font-semibold mb-3">Recent Jobs</h2>
        <div className="space-y-2" data-testid="recent-jobs-list">
          {data?.recent_jobs?.length ? data.recent_jobs.map((j) => <JobRow key={j.id} j={j} />) : (
            <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">
              No jobs yet. Tap <span className="font-semibold text-foreground">+ New Job</span> to register your first article.
            </CardContent></Card>
          )}
        </div>
      </div>

      {canSeeActivity && (
        <div data-testid="recent-activity-section">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base md:text-lg font-semibold flex items-center gap-1.5">
              <Activity className="h-4 w-4 text-primary" /> Recent Activity
            </h2>
            {user?.role === "admin" && (
              <Link to="/activity" className="text-xs font-semibold text-primary flex items-center gap-1" data-testid="view-all-activity-link">
                View all <ArrowRight className="h-3 w-3" />
              </Link>
            )}
          </div>
          <div className="space-y-2" data-testid="recent-activity-list">
            {activity?.length ? activity.map((log) => <ActivityRow key={log.id} log={log} compact />) : (
              <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">No activity recorded yet.</CardContent></Card>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
